import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { Quote, ExternalLink } from "lucide-react";
import { API } from "@/data/catalog";
import { GoldStars } from "@/components/GoldStars";

const GoogleG = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 48 48" aria-hidden="true">
    <path fill="#4285F4" d="M45.1 24.5c0-1.6-.1-3.1-.4-4.5H24v8.5h11.8c-.5 2.8-2 5.1-4.4 6.7v5.5h7.1c4.2-3.8 6.6-9.5 6.6-16.2z" />
    <path fill="#34A853" d="M24 46c6 0 11-2 14.6-5.3l-7.1-5.5c-2 1.3-4.5 2.1-7.5 2.1-5.8 0-10.6-3.9-12.4-9.1H4.3v5.7C7.9 41.1 15.3 46 24 46z" />
    <path fill="#FBBC05" d="M11.6 27.2c-.5-1.3-.7-2.7-.7-4.2s.3-2.9.7-4.2v-5.7H4.3C2.8 16.1 2 19 2 23s.8 6.9 2.3 9.9l7.3-5.7z" />
    <path fill="#EA4335" d="M24 10.7c3.3 0 6.2 1.1 8.5 3.3l6.3-6.3C35 4.1 30 2 24 2 15.3 2 7.9 6.9 4.3 14.1l7.3 5.7c1.8-5.2 6.6-9.1 12.4-9.1z" />
  </svg>
);

export const HeroReviews = () => {
  const { i18n } = useTranslation();
  const it = i18n.language?.startsWith("it");
  const [data, setData] = useState(null);
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    axios.get(`${API}/public/google-reviews`, { params: { lang: i18n.language?.slice(0, 2) } })
      .then((r) => setData(r.data)).catch(() => {});
  }, [i18n.language]);

  const { items, google } = useMemo(() => {
    const g = data?.google;
    const gReviews = (g?.reviews || []).filter((r) => r.text && (r.rating || 0) >= 4);
    const list = gReviews.length
      ? gReviews.map((r, i) => ({ id: `g${i}`, author: r.author, rating: r.rating, text: r.text, meta: r.relative, source: "google" }))
      : (data?.testimonials || []).map((t) => ({ id: t.id, author: t.author, rating: t.rating, text: t.text, meta: [t.business, t.sector].filter(Boolean).join(" · "), source: "admin" }));
    return { items: list, google: g };
  }, [data]);

  useEffect(() => {
    if (items.length < 2) return;
    const id = setInterval(() => setIdx((i) => (i + 1) % items.length), 5500);
    return () => clearInterval(id);
  }, [items.length]);

  if (!items.length) return null;
  const cur = items[idx % items.length];

  return (
    <section className="bg-[#0b0b0b] border-t border-[#D4AF37]/15 py-12 sm:py-16" data-testid="hero-reviews">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
        <div className="inline-flex items-center gap-2.5 rounded-full border border-[#D4AF37]/30 bg-white/[0.03] px-4 py-2" data-testid="hero-reviews-header">
          <GoogleG />
          {google?.rating ? (
            <span className="text-sm text-white/85">
              <span className="font-bold text-white">{Number(google.rating).toFixed(1)}</span>
              <span className="mx-1.5"><GoldStars size={13} gap={1} count={Math.round(google.rating)} /></span>
              <span className="text-white/60">· {google.total} {it ? "recensioni Google" : "Google reviews"}</span>
            </span>
          ) : (
            <span className="text-sm font-semibold text-white/85">{it ? "Dicono di noi" : "What people say"}</span>
          )}
        </div>

        <div className="relative mt-8 min-h-[150px] flex items-center justify-center">
          {items.map((item, i) => (
            <figure
              key={item.id}
              className={`absolute inset-0 flex flex-col items-center justify-center transition-opacity duration-700 ${i === idx % items.length ? "opacity-100" : "opacity-0 pointer-events-none"}`}
              data-testid={i === idx % items.length ? "hero-review-active" : undefined}
              aria-hidden={i !== idx % items.length}
            >
              <Quote className="w-7 h-7 text-[#D4AF37]/50 mb-3" />
              <GoldStars size={16} gap={2} count={item.rating || 5} />
              <blockquote className="mt-4 text-lg sm:text-xl text-white/90 leading-relaxed font-display max-w-2xl">
                "{item.text}"
              </blockquote>
              <figcaption className="mt-4 text-sm text-white/60">
                <span className="font-bold text-white">{item.author}</span>
                {item.meta ? <span> · {item.meta}</span> : null}
                {item.source === "google" && <span className="ml-1.5 text-[#D4AF37]">· {it ? "verificata su Google" : "verified on Google"}</span>}
              </figcaption>
            </figure>
          ))}
        </div>

        {items.length > 1 && (
          <div className="mt-6 flex items-center justify-center gap-2" data-testid="hero-reviews-dots">
            {items.map((_, i) => (
              <button
                key={i}
                type="button"
                onClick={() => setIdx(i)}
                aria-label={`Recensione ${i + 1}`}
                data-testid={`hero-review-dot-${i}`}
                className={`h-2 rounded-full transition-all ${i === idx % items.length ? "w-6 bg-[#D4AF37]" : "w-2 bg-white/25 hover:bg-white/50"}`}
              />
            ))}
          </div>
        )}

        {google?.url && (
          <a href={google.url} target="_blank" rel="noopener noreferrer" data-testid="hero-reviews-google-link"
            className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-[#D4AF37] hover:text-[#E6C766] transition-colors">
            {it ? "Vedi tutte su Google" : "See all on Google"} <ExternalLink className="w-3.5 h-3.5" />
          </a>
        )}
      </div>
    </section>
  );
};
